import { useEffect, useState } from "react";
import { ArrowDown, ArrowUpRight, ChevronDown, Menu, Plus, X } from "lucide-react";
import { Toaster } from "sonner";
import "@/App.css";
import FirmSite from "./FirmSite";
import Reveal from "./components/Reveal";
import OrbitalField from "./components/OrbitalField";
import InquiryModal from "./components/InquiryModal";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxzYXRlbGxpdGUlMjBvcmJpdGFsJTIwZWFydGh8ZW58MHx8fHwxNzg4Mjg2Mzk2fDA&ixlib=rb-4.1.0&q=85";
const ARCHITECTURE_IMAGE =
  "https://images.unsplash.com/photo-1549791084-5f78368b208b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwYXJjaGl0ZWN0dXJhbCUyMGNvbmNyZXRlJTIwbHV4dXJ5fGVufDB8fHx8MTc4ODI4NjM5Nnww&ixlib=rb-4.1.0&q=85";
const GEOMETRY_IMAGE =
  "https://images.unsplash.com/photo-1520529890308-f503006340b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxtaW5pbWFsJTIwYXJjaGl0ZWN0dXJhbCUyMGNvbmNyZXRlJTIwbHV4dXJ5fGVufDB8fHx8MTc4ODI4NjM5Nnww&ixlib=rb-4.1.0&q=85";
const SILICON_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/e7176644-ea79-4c8b-885e-0e2d22806074/images/71ba32a9d71fede7fb970ddcdfdad1808d18650862035da0275b40f76a0b346c.jpeg";
const CLEANROOM_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/e7176644-ea79-4c8b-885e-0e2d22806074/images/114a139c63612e5be0013d0bf9e069e0426baca00fad28cb84a12b9c6d02d80e.jpeg";
const PHOTONIC_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/e7176644-ea79-4c8b-885e-0e2d22806074/images/fe4af33cc4ae46fb98d52b58a07db32019e62d58b89963a3b0ae7616eec34058.jpeg";

const navItems = [
  { label: "Services", href: "#capabilities", id: "services" },
  { label: "Industries", href: "#industries", id: "industries" },
  { label: "Companies", href: "#companies", id: "companies" },
  { label: "Network", href: "#network", id: "network" },
  { label: "About", href: "#about", id: "about" },
  { label: "Insights", href: "#insights", id: "insights" },
  { label: "Contact", href: "#contact", id: "contact" },
];

const capabilities = [
  {
    number: "01",
    title: "Talent Acquisition",
    copy: "Executive, technical, commercial, and specialist talent acquisition for high-growth deep-tech companies.",
  },
  {
    number: "02",
    title: "Company Scaling",
    copy: "Build the organizational infrastructure required to move from startup to serious global company.",
  },
  {
    number: "03",
    title: "People Strategy",
    copy: "Leadership architecture, team development, organizational planning, and strategic hiring.",
  },
  {
    number: "04",
    title: "Growth Strategy",
    copy: "Support the company’s broader mission, impact initiatives, capital planning, and scaling strategy.",
  },
];

const industries = [
  "Artificial Intelligence",
  "Quantum Computing",
  "Aerospace & Space Research",
  "Aviation / UAV",
  "Machine Learning / Robotics",
  "Energy & Data Center Infrastructure",
  "Biotechnology",
];

const lifecycle = ["Pre-Seed", "Seed", "Series A", "Series B", "Series C", "Growth", "Pre-IPO"];
const clients = ["Joby Aviation", "Kraft Heinz", "Grindr", "Moloco", "Raytheon", "Flexport", "Unqork", "OpenAI"];
const processSteps = [
  ["01", "Discover", "Understand the technology, company, capital structure, mission, and growth trajectory."],
  ["02", "Map", "Identify the organizational requirements and talent architecture."],
  ["03", "Access", "Activate the ZUMAIA network and proprietary talent intelligence."],
  ["04", "Build", "Acquire the leaders and specialists required to execute the strategy."],
  ["05", "Scale", "Continue supporting the organization as it grows."],
];

function SectionIntro({ number, label, title, copy, testId }) {
  return (
    <div className="section-intro">
      <Reveal className="section-intro__index">
        <span className="section-number">{number}</span>
        <span className="section-line" />
        <span className="section-label">{label}</span>
      </Reveal>
      <Reveal className="section-intro__body" delay={90}>
        <h2 data-testid={testId}>{title}</h2>
        {copy && <p>{copy}</p>}
      </Reveal>
    </div>
  );
}

function Header({ isScrolled, menuOpen, setMenuOpen, openInquiry }) {
  return (
    <header className={`site-header ${isScrolled ? "site-header--scrolled" : ""}`} data-testid="site-header">
      <a className="brand-mark" href="#home" data-testid="brand-home-link" onClick={() => setMenuOpen(false)}>
        ZUMAIA <span>01</span>
      </a>
      <nav className={`site-nav ${menuOpen ? "site-nav--open" : ""}`} aria-label="Primary navigation">
        {navItems.map((item) => (
          <a
            className="site-nav__link"
            href={item.href}
            data-testid={`nav-${item.id}-link`}
            key={item.id}
            onClick={() => setMenuOpen(false)}
          >
            {item.label}
          </a>
        ))}
        <button className="site-nav__mobile-cta" type="button" data-testid="mobile-conversation-button" onClick={openInquiry}>
          Start a conversation <ArrowUpRight size={15} strokeWidth={1.5} />
        </button>
      </nav>
      <div className="site-header__right">
        <span className="network-status"><i /> Network / Active</span>
        <button className="header-cta" type="button" data-testid="header-conversation-button" onClick={openInquiry}>
          Start a conversation <ArrowUpRight size={15} strokeWidth={1.5} />
        </button>
        <button
          className="menu-toggle"
          type="button"
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={menuOpen}
          data-testid="mobile-menu-toggle"
          onClick={() => setMenuOpen((current) => !current)}
        >
          {menuOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
        </button>
      </div>
    </header>
  );
}

function Hero({ openInquiry }) {
  const handlePointerMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    event.currentTarget.style.setProperty("--parallax-x", x.toFixed(3));
    event.currentTarget.style.setProperty("--parallax-y", y.toFixed(3));
  };

  return (
    <section className="hero" id="home" data-testid="hero-section" onPointerMove={handlePointerMove}>
      <div className="hero__inner">
        <div className="hero__copy">
          <Reveal className="eyebrow-row">
            <span className="eyebrow">ZUMAIA / DEEP-TECH GROWTH PLATFORM</span>
            <span className="eyebrow-meta">US / CH</span>
          </Reveal>
          <h1 data-testid="hero-headline">
            <span className="hero-line-mask"><span className="hero-line">WE BUILD THE</span></span>
            <span className="hero-line-mask"><span className="hero-line">TEAMS BEHIND</span></span>
            <span className="hero-line-mask"><span className="hero-line">THE TECHNOLOGIES</span></span>
            <span className="hero-line-mask"><span className="hero-line">CHANGING THE</span></span>
            <span className="hero-line-mask"><span className="hero-line">WORLD.</span></span>
          </h1>
          <Reveal className="hero__support" delay={650}>
            <p>Deep-Tech Talent Acquisition &amp; Global Company Scaling</p>
            <span className="hero__range">Pre-Seed <b>→</b> Pre-IPO</span>
          </Reveal>
          <Reveal className="hero__actions" delay={760}>
            <button className="button button--gold" type="button" data-testid="hero-build-button" onClick={openInquiry}>
              Build with ZUMAIA <ArrowUpRight size={17} strokeWidth={1.5} />
            </button>
            <a className="text-link" href="#capabilities" data-testid="hero-capabilities-link">
              Explore our capabilities <ArrowDown size={16} strokeWidth={1.5} />
            </a>
          </Reveal>
        </div>
        <div className="hero__visual" aria-label="Orbital network visual reference">
          <OrbitalField />
          <div className="hero__visual-frame">
            <div className="hero__visual-topline"><span>VISUAL REFERENCE / 001</span><span>DEEP-TECH SIGNAL</span></div>
            <div className="media-frame media-frame--hero">
              <img src={HERO_IMAGE} alt="Satellite orbiting above the curve of Earth" />
              <span className="media-crosshair media-crosshair--one" />
              <span className="media-crosshair media-crosshair--two" />
              <span className="media-coordinates">43°N / 2°W</span>
            </div>
            <div className="hero__visual-caption"><span>Access is a strategy.</span><span>A-01 / ORBITAL REACH</span></div>
          </div>
          <div className="hero__visual-index">01 <span>THE SIGNAL</span></div>
        </div>
        <div className="hero__footer">
          <span>Strategic access / 01</span>
          <span>Deep-tech only</span>
          <span>Precision / Speed / Execution</span>
          <span className="scroll-cue"><span /> Scroll to explore</span>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ["Deep-tech talent", "Company scaling", "People strategy", "Global growth", "We get shit done."];
  return (
    <section className="marquee" data-testid="editorial-marquee" aria-label="ZUMAIA capabilities">
      <div className="marquee__track">
        {[...items, ...items].map((item, index) => (
          <span className="marquee__item" key={`${item}-${index}`}>
            {item} <b>•</b>
          </span>
        ))}
      </div>
    </section>
  );
}

function Capabilities() {
  return (
    <section className="section capabilities-section" id="capabilities" aria-labelledby="capabilities-heading">
      <div className="section-inner">
        <SectionIntro
          number="02"
          label="What ZUMAIA does"
          title={<>Not a recruitment agency.<br /><em>A scaling partner.</em></>}
          copy="ZUMAIA is a niche accelerator for deep-tech startups and deep-tech corporations. We combine talent acquisition with corporate and people scaling strategies specifically designed for deep-tech innovation."
          testId="capabilities-heading"
        />
        <div className="capability-grid">
          {capabilities.map((capability, index) => (
            <Reveal className={`capability-card capability-card--${index + 1}`} delay={index * 90} key={capability.number}>
              <article data-testid={`capability-${capability.number}`}>
                <div className="card-topline"><span>{capability.number}</span><Plus size={17} strokeWidth={1.2} /></div>
                <h3>{capability.title}</h3>
                <p>{capability.copy}</p>
                <span className="card-arrow"><ArrowUpRight size={17} strokeWidth={1.4} /></span>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal className="pull-statement" delay={180}>
          <span className="pull-statement__mark">“</span>
          <p>We don&apos;t just fill positions.<br /><em>We build the infrastructure for growth.</em></p>
        </Reveal>
      </div>
    </section>
  );
}

function Industries() {
  return (
    <section className="section industries-section" id="industries" aria-labelledby="industries-heading">
      <div className="section-inner">
        <SectionIntro
          number="03"
          label="The deep-tech only platform"
          title={<>We only work where<br /><em>technology gets hard.</em></>}
          copy="The harder the problem, the more consequential the team behind it. ZUMAIA works inside a deliberately narrow field of technical ambition."
          testId="industries-heading"
        />
        <div className="industries-layout">
          <ol className="industry-list">
            {industries.map((industry, index) => (
              <Reveal delay={index * 55} key={industry}>
                <li data-testid={`industry-${index + 1}`}><span>0{index + 1}</span><strong>{industry}</strong><ArrowUpRight size={17} strokeWidth={1.3} /></li>
              </Reveal>
            ))}
          </ol>
          <div className="signal-gallery" data-testid="industry-signal-gallery">
            <figure className="media-frame media-frame--tall">
              <img src={ARCHITECTURE_IMAGE} alt="Monolithic architectural structure in a high contrast light" loading="lazy" />
              <figcaption><span>Signal / 01</span><span>Structures that hold</span></figcaption>
            </figure>
            <figure className="media-frame media-frame--small">
              <img src={GEOMETRY_IMAGE} alt="Precision geometric architecture with strong shadow lines" loading="lazy" />
              <figcaption><span>Signal / 02</span><span>Precision under pressure</span></figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}

function Lifecycle() {
  return (
    <section className="section lifecycle-section" id="lifecycle" aria-labelledby="lifecycle-heading">
      <div className="section-inner">
        <SectionIntro
          number="04"
          label="Company lifecycle"
          title={<>From first round<br /><em>to global scale.</em></>}
          copy="ZUMAIA adapts its talent and scaling strategy according to the company’s stage, with its greatest client concentration across Series A–C."
          testId="lifecycle-heading"
        />
        <div className="lifecycle-track" data-testid="company-lifecycle-track">
          <div className="lifecycle-line" />
          <ol>
            {lifecycle.map((stage, index) => (
              <li className={stage === "Series A" || stage === "Series B" || stage === "Series C" ? "is-focus" : ""} key={stage}>
                <span className="lifecycle-dot" />
                <span className="lifecycle-number">0{index + 1}</span>
                <strong>{stage}</strong>
                {stage === "Series A" && <em>Greatest client concentration</em>}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function Network() {
  return (
    <section className="section network-section" id="network" aria-labelledby="network-heading">
      <div className="section-inner">
        <SectionIntro
          number="05"
          label="The network"
          title={<>Access changes<br /><em>what is possible.</em></>}
          copy="An extensive network spanning Fortune 100 companies, deep-tech startups, investors, executives, and specialized technical talent."
          testId="network-heading"
        />
        <div className="network-layout">
          <div className="network-stat" data-testid="network-stat">
            <strong>8.3M<span>+</span></strong>
            <span>People in our global network</span>
            <p>Proprietary reach.<br />Strategic access.<br />Meaningful deal flow.</p>
          </div>
          <div className="network-map" data-testid="network-visual" role="img" aria-label="Abstract global network map connecting technical talent and decision makers">
            <div className="network-map__header"><span>GLOBAL NETWORK / LIVE VIEW</span><span>8.3M+ NODES</span></div>
            <div className="network-map__legend" aria-label="Network route color legend"><span><i className="legend-swatch legend-swatch--cyan" />Specialized talent</span><span><i className="legend-swatch legend-swatch--champagne" />Executive access</span><span><i className="legend-swatch legend-swatch--amethyst" />Investor reach</span><span><i className="legend-swatch legend-swatch--rose" />Company scale</span></div>
            <svg fill="none" viewBox="0 0 900 480" role="presentation">
              <path className="map-graticule" d="M0 120 H900 M0 240 H900 M0 360 H900 M180 0 V480 M360 0 V480 M540 0 V480 M720 0 V480" />
              <line className="map-equator" x1="0" y1="240" x2="900" y2="240" />
              <path className="map-land" d="M96 149c38-34 76-42 118-25l33 28 34 9 14 41-28 18-14 36-48 3-28-19-47 3-30-26-18-30z" />
              <path className="map-land" d="M303 256l42 6 28 39-6 54-29 20-17 56-24-18 4-53-27-34 12-34z" />
              <path className="map-land" d="M452 139l45-22 52 5 34 23 71 2 37 36-19 29-57-3-41 28-48-14-26 19-29-28 10-39z" />
              <path className="map-land" d="M573 264l51-11 46 19 32 39-20 40-31 5-32-25-31 2-20-33z" />
              <path className="map-land" d="M717 189l37 9 30 31-18 35-42-12-24-26z" />
              <path className="map-route map-route--champagne" d="M174 172C328 68 550 105 730 213" />
              <path className="map-route map-route--cyan" d="M202 194C368 359 530 340 714 215" />
              <path className="map-route map-route--amethyst" d="M336 293C446 171 617 151 735 218" />
              <path className="map-route map-route--rose" d="M174 172C362 410 585 328 730 213" />
              <g className="map-nodes">
                <circle className="map-node map-node--champagne" cx="174" cy="172" r="6" /><circle className="map-node map-node--cyan" cx="347" cy="144" r="5" /><circle className="map-node map-node--amethyst" cx="495" cy="128" r="6" /><circle className="map-node map-node--emerald" cx="608" cy="172" r="5" /><circle className="map-node map-node--rose" cx="730" cy="213" r="7" /><circle className="map-node map-node--cyan" cx="202" cy="194" r="5" /><circle className="map-node map-node--champagne" cx="368" cy="310" r="6" /><circle className="map-node map-node--emerald" cx="530" cy="340" r="5" /><circle className="map-node map-node--rose" cx="714" cy="215" r="5" />
              </g>
            </svg>
            <div className="network-map__labels"><span>North America</span><span>Europe</span><span>Asia</span><span>Middle East</span></div>
            <span className="network-map__footnote">Representational reach visualization / not a geographic density map</span>
          </div>
        </div>
        <div className="proof-intro">
          <span>05 / VISUAL REFERENCE</span>
          <p>Different disciplines. One standard: precision that compounds at scale.</p>
        </div>
        <div className="proof-gallery" data-testid="deep-tech-photo-gallery">
          <figure className="proof-card proof-card--wafer" data-testid="photo-panel-wafer">
            <img src={SILICON_IMAGE} alt="Iridescent silicon wafer on precision semiconductor tooling" loading="lazy" />
            <figcaption className="proof-card__caption"><span>01 / Silicon substrate</span><span>Compute / 3nm</span></figcaption>
            <span className="proof-card__datum">FIELD NOTE / 001</span>
          </figure>
          <figure className="proof-card proof-card--cleanroom" data-testid="photo-panel-cleanroom">
            <img src={CLEANROOM_IMAGE} alt="Semiconductor cleanroom process chamber with precision equipment" loading="lazy" />
            <figcaption className="proof-card__caption"><span>02 / Cleanroom synthesis</span><span>Systems / Class 100</span></figcaption>
            <span className="proof-card__datum">FIELD NOTE / 002</span>
          </figure>
          <figure className="proof-card proof-card--photonic" data-testid="photo-panel-photonic">
            <img src={PHOTONIC_IMAGE} alt="Photonic laser optics and fiber assemblies on a precision optical bench" loading="lazy" />
            <figcaption className="proof-card__caption"><span>03 / Photonic systems</span><span>Signal / Optical edge</span></figcaption>
            <span className="proof-card__datum">FIELD NOTE / 003</span>
          </figure>
        </div>
      </div>
    </section>
  );
}

function Ecosystem() {
  return (
    <section className="section ecosystem-section" id="companies" aria-labelledby="ecosystem-heading">
      <div className="section-inner">
        <SectionIntro
          number="06"
          label="Client ecosystem"
          title={<>Trusted by companies<br /><em>building what comes next.</em></>}
          copy="A restrained view of the ecosystem referenced in the ZUMAIA company biography. The work stays confidential; the names speak to the range of ambition."
          testId="ecosystem-heading"
        />
        <div className="client-wall" data-testid="client-wall">
          {clients.map((client, index) => (
            <Reveal delay={index * 65} key={client}>
              <span className="client-name" data-testid={`client-${index + 1}`}>{client}</span>
            </Reveal>
          ))}
          <Reveal className="client-name client-name--more" delay={520}><span>+ approximately 200 similar companies</span></Reveal>
        </div>
      </div>
    </section>
  );
}

function Profile() {
  return (
    <section className="section profile-section" id="profile" aria-labelledby="profile-heading">
      <div className="section-inner">
        <SectionIntro
          number="07"
          label="The client profile"
          title={<>Built for ambitious<br /><em>companies.</em></>}
          copy="ZUMAIA works with highly funded technology companies operating at significant stages of growth."
          testId="profile-heading"
        />
        <div className="profile-layout">
          <div className="profile-valuation" data-testid="client-valuation-range"><strong>$250M <span>—</span> $50B</strong><p>Typical client valuation range</p></div>
          <div className="profile-traits">
            <Reveal><article><span>01</span><h3>High-growth</h3><p>Companies moving rapidly from innovation to scale.</p></article></Reveal>
            <Reveal delay={90}><article><span>02</span><h3>Deep-tech</h3><p>Technology requiring specialized technical and strategic talent.</p></article></Reveal>
            <Reveal delay={180}><article><span>03</span><h3>Capitalized</h3><p>Companies with the resources and ambition to build globally.</p></article></Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Difference() {
  return (
    <section className="section difference-section" id="difference" aria-labelledby="difference-heading">
      <div className="section-inner">
        <SectionIntro
          number="08"
          label="Why ZUMAIA"
          title={<>The difference is<br /><em>execution.</em></>}
          copy="Talent is only one component of company growth. We approach hiring within the larger context of the company’s mission, capital strategy, organizational structure, and growth trajectory."
          testId="difference-heading"
        />
        <div className="comparison" data-testid="execution-comparison">
          <div className="comparison-row comparison-row--traditional"><span>Traditional recruitment</span><strong>Candidate <b>→</b> Position <b>→</b> Placement</strong></div>
          <div className="comparison-row comparison-row--zumaia"><span>ZUMAIA</span><strong>Technology <b>→</b> Strategy <b>→</b> Organization <b>→</b> Leadership <b>→</b> Talent <b>→</b> Scale</strong></div>
        </div>
        <Reveal className="difference-note" delay={150}><span>“</span><p>The brief is never just the role.<br /><em>The brief is the company becoming what it needs to be.</em></p></Reveal>
      </div>
    </section>
  );
}

function Global() {
  return (
    <section className="section global-section" id="global" aria-labelledby="global-heading">
      <div className="section-inner">
        <SectionIntro
          number="09"
          label="Global"
          title={<>Global talent.<br />Global companies.<br /><em>Global scale.</em></>}
          copy="ZUMAIA operates internationally across the United States and Switzerland, connecting companies with specialized global talent."
          testId="global-heading"
        />
        <div className="global-visual" data-testid="global-visual" role="img" aria-label="Global connection routes between North America, Europe, Asia, and the Middle East">
          <div className="global-visual__topline"><span>OPERATING ACROSS TIME ZONES</span><span>US / CH / GLOBAL</span></div>
          <div className="global-visual__sphere"><span className="global-ring global-ring--one" /><span className="global-ring global-ring--two" /><span className="global-ring global-ring--three" />
            <span className="global-node global-node--na global-node--cyan">North America</span><span className="global-node global-node--eu global-node--champagne">Europe</span><span className="global-node global-node--asia global-node--rose">Asia</span><span className="global-node global-node--me global-node--emerald">Middle East</span>
            <svg fill="none" viewBox="0 0 900 280" role="presentation">
              <path className="global-graticule" d="M0 70 H900 M0 140 H900 M0 210 H900 M180 0 V280 M360 0 V280 M540 0 V280 M720 0 V280" />
              <line className="global-equator" x1="0" y1="140" x2="900" y2="140" />
              <g className="global-landmasses" transform="translate(0 -54) scale(1 0.68)">
                <path className="global-land" d="M96 149c38-34 76-42 118-25l33 28 34 9 14 41-28 18-14 36-48 3-28-19-47 3-30-26-18-30z" />
                <path className="global-land" d="M303 256l42 6 28 39-6 54-29 20-17 56-24-18 4-53-27-34 12-34z" />
                <path className="global-land" d="M452 139l45-22 52 5 34 23 71 2 37 36-19 29-57-3-41 28-48-14-26 19-29-28 10-39z" />
                <path className="global-land" d="M573 264l51-11 46 19 32 39-20 40-31 5-32-25-31 2-20-33z" />
                <path className="global-land" d="M717 189l37 9 30 31-18 35-42-12-24-26z" />
              </g>
              <path className="global-path global-path--champagne" d="M112 178C251 24 472 28 775 141" /><path className="global-path global-path--cyan" d="M112 178C277 260 500 248 775 141" /><path className="global-path global-path--amethyst" d="M242 177C400 84 606 87 775 141" /><path className="global-path global-path--rose" d="M112 178C380 160 565 232 775 141" />
            </svg>
          </div>
          <p className="global-visual__caption">The most important talent is rarely in one room. We make the room larger.</p>
        </div>
      </div>
    </section>
  );
}

function Approach() {
  return (
    <section className="section approach-section" id="approach" aria-labelledby="approach-heading">
      <div className="section-inner">
        <SectionIntro
          number="10"
          label="The ZUMAIA approach"
          title={<>Five moves.<br /><em>One trajectory.</em></>}
          copy="The work is structured, direct, and built to keep pace with the companies we support."
          testId="approach-heading"
        />
        <ol className="approach-list" data-testid="approach-list">
          {processSteps.map(([number, title, copy], index) => (
            <Reveal delay={index * 90} key={number}>
              <li><span className="approach-number">{number}</span><h3>{title}</h3><p>{copy}</p><ArrowUpRight className="approach-arrow" size={18} strokeWidth={1.3} /></li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Insights() {
  return (
    <section className="section insights-section" id="insights" aria-labelledby="insights-heading">
      <div className="section-inner insights-inner">
        <Reveal><div className="section-intro__index"><span className="section-number">11</span><span className="section-line" /><span className="section-label">Insights / editorial layer</span></div></Reveal>
        <Reveal className="insights-copy" delay={90}><h2 id="insights-heading">The ideas shaping<br /><em>deep-tech growth.</em></h2><p>A future home for ZUMAIA perspectives on talent, capital, leadership, and the companies building what comes next.</p><span className="insights-status">Editorial platform / coming into focus</span></Reveal>
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="section about-section" id="about" aria-labelledby="about-heading">
      <div className="section-inner">
        <SectionIntro
          number="12"
          label="About ZUMAIA"
          title={<>Built for the companies<br /><em>that build the future.</em></>}
          copy="ZUMAIA is a privately held deep-tech talent acquisition, company scaling, and strategic advisory firm operating across the United States and Switzerland."
          testId="about-heading"
        />
        <div className="about-layout">
          <div className="about-manifesto"><span>We get shit done.</span><p>Not a slogan. A standard for the work.</p></div>
          <div className="about-details"><div className="about-detail"><span>Company type</span><strong>Privately held</strong></div><div className="about-detail"><span>Operating across</span><strong>United States / Switzerland</strong></div><div className="about-detail about-detail--valuation" data-testid="company-valuation"><span>Company valuation</span><strong>$32.5M</strong></div></div>
        </div>
      </div>
    </section>
  );
}

function ClosingCta({ openInquiry }) {
  return (
    <section className="closing-cta" id="contact" data-testid="closing-cta">
      <div className="closing-cta__inner">
        <Reveal><span className="eyebrow">ZUMAIA / THE NEXT MOVE</span></Reveal>
        <Reveal delay={110}><h2>Build the team<br /><em>that builds the future.</em></h2></Reveal>
        <Reveal className="closing-cta__footer" delay={220}><p>Tell us what you&apos;re building.</p><div><button className="button button--gold" type="button" data-testid="cta-conversation-button" onClick={openInquiry}>Start a conversation <ArrowUpRight size={17} strokeWidth={1.5} /></button><a className="text-link text-link--light" href="#about" data-testid="cta-about-link">Meet ZUMAIA <ArrowUpRight size={16} strokeWidth={1.5} /></a></div></Reveal>
      </div>
    </section>
  );
}

function Footer() {
  const footerLinks = [
    ["Capabilities", "#capabilities", "footer-capabilities-link"],
    ["Industries", "#industries", "footer-industries-link"],
    ["Companies", "#companies", "footer-companies-link"],
    ["Network", "#network", "footer-network-link"],
    ["About", "#about", "footer-about-link"],
    ["Insights", "#insights", "footer-insights-link"],
    ["Contact", "#contact", "footer-contact-link"],
  ];
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="site-footer__top"><a className="footer-brand" href="#home" data-testid="footer-brand-link">ZUMAIA</a><span className="footer-coordinate">DEEP-TECH GROWTH PLATFORM / 01</span></div>
      <div className="site-footer__middle"><nav aria-label="Footer navigation">{footerLinks.map(([label, href, testId]) => <a href={href} data-testid={testId} key={label}>{label}</a>)}</nav><div className="footer-note"><span>Deep-Tech Talent Acquisition &amp; Global Company Scaling</span><span>United States | Switzerland</span></div></div>
      <div className="site-footer__bottom"><span>© {new Date().getFullYear()} ZUMAIA</span><span>Precision / Access / Execution</span><a href="#home" data-testid="footer-top-link">Back to top <ChevronDown size={14} strokeWidth={1.3} /></a></div>
    </footer>
  );
}

function LegacyApp() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const openInquiry = () => {
    setMenuOpen(false);
    setInquiryOpen(true);
  };

  return (
    <div className="site-shell">
      <Header isScrolled={isScrolled} menuOpen={menuOpen} setMenuOpen={setMenuOpen} openInquiry={openInquiry} />
      <main>
        <Hero openInquiry={openInquiry} />
        <Marquee />
        <Capabilities />
        <Industries />
        <Lifecycle />
        <Network />
        <Ecosystem />
        <Profile />
        <Difference />
        <Global />
        <Approach />
        <Insights />
        <About />
        <ClosingCta openInquiry={openInquiry} />
      </main>
      <Footer />
      <InquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} />
      <Toaster position="top-right" toastOptions={{ className: "zumaia-toast" }} />
    </div>
  );
}


export default function App() {
  return <FirmSite />;
}
